import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import { useApiQuery, useTeam } from '@vrplatform/data';
import { type Maybe, sortBy } from '@vrplatform/utils';
import { Avatar, Box, Center } from '@mantine/core';

type Props = {
  bookingChannels: Maybe<string[]>;
  setBookingChannels: (bookingChannels: string[]) => void;
};

export const BookingChannelFilter = ({
  bookingChannels: initial,
  setBookingChannels,
}: Props) => {
  const [{ id: teamId }] = useTeam();

  const bookingChannels = initial || [];
  const gate = useSelectQueryGate({ value: bookingChannels });

  const queryData = useApiQuery(
    async (api, signal) => {
      const result = await api.GET('/booking-channels', {
        params: {},
        signal,
      });

      if (result.error) throw result.error;

      const items = result.data.data.map<SelectItem>((channel) => ({
        value: channel.name,
        label: channel.name,
        icon: (
          <Center>
            {channel.icon ? (
              <Avatar
                src={channel.icon}
                size={16}
                sx={(theme) => ({
                  borderRadius: '50%',
                  border: '1px solid',
                  borderColor: channel.color ?? 'gray',
                  backgroundColor: channel.color ?? theme.colors.gray[0],
                })}
              />
            ) : (
              <Box
                w={16}
                h={16}
                sx={{
                  borderRadius: '50%',
                  backgroundColor: 'transparent',
                }}
              />
            )}{' '}
          </Center>
        ),
      }));

      return sortBy(items, 'label');
    },
    {
      queryGroup: 'reservations',
      uniqueQueryKey: 'booking-channels',
      variables: {},
      skip: !teamId || !gate.shouldFetchAny,
    }
  );

  const value = gate.hasValue
    ? queryData.data?.filter((entry) =>
        bookingChannels.includes(entry.value)
      ) || []
    : [];

  return (
    <Filter.Select
      value={value}
      setValue={(next) => setBookingChannels(next.map((item) => item.value))}
      label="Booking Channel"
      type="multiple"
      withinPortal
      onOpenChange={gate.onOpenChange}
      data={{
        options: queryData.data || [],
        loading: queryData.isLoading,
        error: queryData.error,
        sort: null,
      }}
    />
  );
};
